/**
 * cruisePortCalls.ts — クルーズ船の寄港予定（r-cal /cruise の「寄港予定」タブ）の純粋関数と API 呼び出し（2026-10-31）
 *
 * 沖縄県オープンデータから取り込んだ予定は自動で公開し、載らなくなった予定は自動で非公開にする（2026-11-01 前田さん）。
 * 公開中の予定だけが LP の「寄港スケジュールで選ぶ」に出る。スタッフが非公開にした予定は、次の取り込みでも公開し直さない。保存先は sys/api/cruise_port_calls.php（GET 公開 / scope=admin と POST はスタッフの Bearer）。
 * 正: booking-platform/md/pricing.md「クルーズ船の予約 — 2026-10-31 の決定」 / ページ仕様: md/master_md/page-cruise.md
 */
import { SYS_API_BASE } from '../constants/calendar';
import { withStaffAuth } from './internalApiAuth';

const PORT_API = `${SYS_API_BASE}/cruise_port_calls.php`;

export type PortCallStatus = 'draft' | 'published' | 'hidden';

export interface PortCallPublicValues {
  arrive_time: string;
  depart_date: string;
  depart_time: string;
  overnight: number;
}

export interface PortCall {
  id: number;
  date: string;
  ship: string;
  arrive: string;
  depart_date: string;
  depart: string;
  overnight: boolean;
  /** 那覇のクルーズ船プラン（sys/cruise/plan_lib.php。当たらない予定・宮古は null）とプランの時間（2026-11-02） */
  plan?: 'morning' | 'midday' | 'afternoon' | 'overnight' | null;
  plan_pickup?: string | null;
  plan_return?: string | null;
  status: PortCallStatus;
  source: 'bodik' | 'manual';
  berth: string | null;
  route: string | null;
  prev_port: string | null;
  next_port: string | null;
  passengers: number | null;
  pending: PortCallPublicValues | null;
  missing_in_source: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
  imported_at: string | null;
}

export type ImportMode = 'weekly' | 'monthly' | 'daily';

export interface ImportSettings {
  /** weekly（初期値・毎週月曜日）／monthly／daily。確かめる日に更新日を見て、変わっていれば取り込む */
  mode: ImportMode;
  /** ISO の曜日 1=月〜7=日 */
  weekdays: number[];
  days: number[];
  notify_stores: string[];
}

export interface ImportState {
  settings: ImportSettings;
  last_run_at: string | null;
  last_result: string | null;
  last_error: string | null;
  last_source_name: string | null;
  last_source_modified: string | null;
  last_imported_modified: string | null;
  /** CSV の更新日から何日たったか。stale_threshold_days 以上でスタッフにメール */
  stale_days: number | null;
  stale_threshold_days: number;
}

export interface PortCallsAdmin {
  calls: PortCall[];
  import: ImportState;
  source: { label: string; url: string };
}

/** 確かめてほしい予定か（最新の予定に載らなくなった・自動公開の前の下書き・確認待ちの時刻が残っている） */
export function needsReview(c: PortCall): boolean {
  return c.status === 'draft' || c.pending !== null || c.missing_in_source;
}

/** 一覧の絞り込み */
export type PortCallFilter = 'review' | 'published' | 'hidden' | 'all';

export function filterCalls(calls: PortCall[], filter: PortCallFilter): PortCall[] {
  switch (filter) {
    case 'review': return calls.filter(needsReview);
    case 'published': return calls.filter(c => c.status === 'published');
    case 'hidden': return calls.filter(c => c.status === 'hidden');
    default: return calls;
  }
}

/** 一覧の「状態」欄の文言 */
export function statusLabel(c: PortCall): string {
  if (c.missing_in_source) {
    return c.status === 'published' ? '載っていないが公開中（スタッフが公開）' : '最新の予定に載っていない（自動で非公開・取り消し？）';
  }
  if (c.pending) return '時刻が変わった（確認待ち）';
  if (c.status === 'draft') return '未公開（次の取り込みで公開）';
  if (c.status === 'hidden') return '非公開（スタッフ）';
  return '公開中';
}

/** 「入港 08:00 → 出港 18:00」。泊まりは出港日も付ける */
export function timesLabel(v: { date: string; arrive: string; depart_date: string; depart: string }): string {
  const dep = v.depart_date !== v.date ? `${v.depart_date.slice(5).replace('-', '/')} ${v.depart}` : v.depart;
  return `入港 ${v.arrive} → 出港 ${dep}`;
}

/** 確認待ちの時刻（CSV の新しい値）の文言 */
export function pendingLabel(c: PortCall): string | null {
  if (!c.pending) return null;
  return timesLabel({ date: c.date, arrive: c.pending.arrive_time, depart_date: c.pending.depart_date, depart: c.pending.depart_time });
}

const WEEKDAY_NAMES = ['', '月', '火', '水', '木', '金', '土', '日'];
export const WEEKDAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7].map(n => ({ value: n, label: `${WEEKDAY_NAMES[n]}曜日` }));

/** 確かめる日の設定の文言 */
export function settingsLabel(s: ImportSettings): string {
  const when = s.mode === 'daily' ? '毎日'
    : s.mode === 'monthly' ? `毎月 ${s.days.join('・')} 日`
      : `毎週 ${s.weekdays.map(n => WEEKDAY_NAMES[n]).join('・')}曜日`;
  return `${when}に更新を確かめ、変わっていれば取り込む`;
}

/** 更新日が長く変わっていないか（スタッフにメールが届く状態） */
export function isSourceStale(i: Pick<ImportState, 'stale_days' | 'stale_threshold_days' | 'last_source_modified'>): boolean {
  if (!i.last_source_modified) return false;
  return i.stale_days !== null && i.stale_days >= i.stale_threshold_days;
}

/** '10, 20' → [10, 20]。1〜28 以外・数字でないものがあれば null */
export function parseDays(raw: string): number[] | null {
  const parts = raw.split(/[,、・\s]+/).filter(Boolean);
  if (parts.length === 0) return null;
  const days: number[] = [];
  for (const p of parts) {
    if (!/^\d+$/.test(p)) return null;
    const d = Number(p);
    if (d < 1 || d > 28) return null;
    if (!days.includes(d)) days.push(d);
  }
  return days.sort((a, b) => a - b);
}

function authHeaders(): HeadersInit {
  let token: string | null = null;
  try { token = window.localStorage.getItem('authToken'); } catch { token = null; }
  return withStaffAuth({ 'Content-Type': 'application/json' }, token);
}

async function readJson(res: Response): Promise<any> {
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export async function fetchPortCallsAdmin(store: string, from: string, to: string): Promise<PortCallsAdmin> {
  const res = await fetch(`${PORT_API}?store=${store}&scope=admin&from=${from}&to=${to}`, { headers: authHeaders() });
  const body = await readJson(res);
  return { calls: body.calls || [], import: body.import, source: body.source };
}

async function post(store: string, payload: Record<string, unknown>): Promise<any> {
  const res = await fetch(PORT_API, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ store, ...payload }) });
  return readJson(res);
}

/** 公開・非公開。サーバが読み直して確かめた件数が合わなければ失敗にする */
export async function setPortCallsStatus(store: string, ids: number[], action: 'publish' | 'hide'): Promise<number> {
  const body = await post(store, { action, ids });
  if (body.verified !== ids.length) throw new Error(`保存を確認できませんでした（${body.verified}/${ids.length} 件）`);
  return body.verified;
}

export interface PortCallInput {
  id?: number;
  date: string;
  ship: string;
  arrive: string;
  depart_date: string;
  depart: string;
  status: PortCallStatus;
}

export async function savePortCall(store: string, call: PortCallInput): Promise<PortCall> {
  const body = await post(store, { action: 'save', call });
  return body.call;
}

export async function deletePortCall(store: string, id: number): Promise<void> {
  await post(store, { action: 'delete', id });
}

export async function savePortCallSettings(store: string, settings: Pick<ImportSettings, 'mode' | 'weekdays' | 'days'>): Promise<ImportSettings> {
  const body = await post(store, { action: 'settings', settings });
  return body.settings;
}

export async function importPortCallsNow(store: string): Promise<any> {
  const body = await post(store, { action: 'import' });
  return body.result;
}
